import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import K4MHero from "./components/K4MHero";
import K4MSpecs from "./components/K4MSpecs";
import K4MAdvantages from "./components/K4MAdvantages";
import K4MQuoteForm from "./components/K4MQuoteForm";
import PageSEO from "@/components/feature/PageSEO";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://chirunnexa.com";

const foundryJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Precision Engine Block Manufacturing: 1:1 OEM Quality | Chirunnexa Foundry",
    "url": `${SITE_URL}/foundry`,
    "description": "One-stop engine block manufacturing: integrated foundry and CNC machining. Renault K4M 1.6 16V wholesale. Reverse engineering, EAC & ISO certified. B2B supply from Guangzhou.",
    "inLanguage": "en",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
        { "@type": "ListItem", "position": 2, "name": "Engine Block Foundry", "item": `${SITE_URL}/foundry` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Renault K4M 1.6 16V Long Block Engine — Wholesale",
    "description": "Brand new Renault K4M bare engine wholesale. OEM-equivalent quality, 100% new components, zero salvaged parts. Applications: Renault Duster, Logan, Megane, Sandero, Lada Largus. Manufactured in China with EAC & ISO 9001:2015 certification.",
    "url": `${SITE_URL}/foundry`,
    "brand": {
      "@type": "Brand",
      "name": "Renault OEM Replacement",
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "Chirunnexa",
      "url": `${SITE_URL}/`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Guangzhou",
        "addressRegion": "Guangdong",
        "addressCountry": "CN",
      },
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "seller": { "@type": "Organization", "name": "Chirunnexa" },
    },
    "additionalProperty": [
      { "@type": "PropertyValue", "name": "Engine Code", "value": "K4M" },
      { "@type": "PropertyValue", "name": "Displacement", "value": "1598cc (1.6L)" },
      { "@type": "PropertyValue", "name": "Valvetrain", "value": "DOHC 16V" },
      { "@type": "PropertyValue", "name": "Bore x Stroke", "value": "79.5mm x 80.5mm" },
      { "@type": "PropertyValue", "name": "Compression Ratio", "value": "9.5:1" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Precision Engine Block Manufacturing & Reverse Engineering",
    "description": "One-stop engine block manufacturing: integrated in-house foundry and CNC machining lines. Sample-based reverse engineering — no OEM blueprints needed. 3D scanning, 1:1 OEM-compliant replacement. EAC & ISO 9001:2015 certified. Mass production capability for Renault K4M and other engine blocks.",
    "url": `${SITE_URL}/foundry`,
    "provider": {
      "@type": "Organization",
      "name": "Chirunnexa",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Guangzhou",
        "addressRegion": "Guangdong",
        "addressCountry": "CN",
      },
    },
    "serviceType": "Engine Block Manufacturing",
    "areaServed": ["Global", "RU", "KZ", "DE", "AE", "PL"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Engine Block Manufacturing Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Reverse Engineering & Custom Tooling" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mass Production — Renault K4M Series" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Integrated Foundry + CNC Machining" } },
      ],
    },
  },
];

export default function K4MPage() {
  const [lang, setLang] = useState<"en" | "ru">("en");

  // Inject canonical tag — solidifies /foundry as the one true URL for Google
  useEffect(() => {
    let canonical = document.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${SITE_URL}/foundry`;
    return () => {
      if (canonical) canonical.href = `${SITE_URL}/`;
    };
  }, []);

  const handleLangSwitch = (l: "en" | "ru") => {
    if (l === "ru") {
      window.location.href = "/ru/foundry";
    } else {
      setLang("en");
    }
  };

  return (
    <div style={{ background: "#0a0f1e" }}>
      <PageSEO
        title="Precision Engine Block Manufacturing: 1:1 OEM Quality | Chirunnexa Foundry"
        description="One-stop engine block manufacturing from Guangzhou: integrated foundry and CNC machining. Renault K4M 1.6 16V wholesale, reverse engineering, 3D scanning. EAC & ISO 9001:2015 certified. B2B quote within 24h."
        keywords="engine block foundry China, Renault K4M wholesale, precision engine block manufacturing, reverse engineering auto parts, cast iron engine block supplier, OEM engine block China, K4M long block wholesale"
        canonical="/foundry"
        hreflangRu="/ru/foundry"
        jsonLd={foundryJsonLd}
      />

      {/* ── Top Nav Bar ── */}
      <nav
        className="sticky top-0 z-50 flex items-center justify-between px-6 py-4"
        style={{ background: "rgba(10,15,30,0.95)", borderBottom: "1px solid rgba(255,255,255,0.07)", backdropFilter: "blur(12px)" }}
      >
        <Link
          to="/"
          className="flex items-center gap-3 cursor-pointer"
          style={{ textDecoration: "none" }}
        >
          <div
            className="w-7 h-7 flex items-center justify-center rounded"
            style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" }}
          >
            <i className="ri-settings-3-line text-xs text-white" />
          </div>
          <span className="text-xs font-bold tracking-widest text-white" style={{ letterSpacing: "0.15em" }}>
            CHIRUNNEXA
          </span>
        </Link>

        <div className="flex items-center gap-4">
          {/* Language toggle */}
          <div
            className="flex items-center rounded-full p-1"
            style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}
          >
            {(["en", "ru"] as const).map((l) => (
              <button
                key={l}
                onClick={() => handleLangSwitch(l)}
                className="px-4 py-1.5 rounded-full text-xs font-bold tracking-widest transition-all duration-200 cursor-pointer whitespace-nowrap"
                style={{
                  background: lang === l ? "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" : "transparent",
                  color: lang === l ? "#ffffff" : "rgba(255,255,255,0.45)",
                  letterSpacing: "0.1em",
                }}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          <a
            href="#k4m-quote"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold tracking-widest whitespace-nowrap cursor-pointer text-white transition-all hover:brightness-110"
            style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", letterSpacing: "0.12em", textDecoration: "none" }}
          >
            <i className="ri-mail-send-line text-sm" />
            {lang === "ru" ? "Запросить прайс" : "Request Quote"}
          </a>
        </div>
      </nav>

      {/* ── Page Sections ── */}
      <K4MHero lang={lang} />
      <K4MSpecs lang={lang} />
      <K4MAdvantages lang={lang} />
      <K4MQuoteForm lang={lang} />

      {/* ── Footer ── */}
      <footer
        className="py-8 px-6"
        style={{ background: "#0a0f1e", borderTop: "1px solid rgba(255,255,255,0.06)" }}
      >
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: "rgba(255,255,255,0.25)" }}>
            &copy; {new Date().getFullYear()} Chirunnexa — Renault K4M Engine Wholesale | Guangzhou Auto Parts Export Hub
          </p>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-xs transition-colors duration-200 cursor-pointer" style={{ color: "rgba(255,255,255,0.25)", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#F59E0B")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.25)")}
            >
              ← Back to Main Site
            </Link>
            <a href="mailto:chirunnexa@gmail.com" className="text-xs transition-colors duration-200 cursor-pointer" style={{ color: "rgba(255,255,255,0.25)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#F59E0B")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.25)")}
            >
              chirunnexa@gmail.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
